/**
 * AutomatiseringPage — toezichtsvenster op alle crons en agents.
 *
 * Leeslaag op GET /api/beheer/crons: GitHub Actions (live laatste run) en de
 * Vercel-crons van deze app. Brein-regel: menselijk toezicht, niet menselijke
 * uitvoering — dit venster is wat de mens overhoudt als de uitvoering
 * geautomatiseerd is.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface WorkflowRow {
  name: string;
  path: string;
  state: string;
  description: string;
  schedules: string[];
  lastRun: {
    status: string | null;
    conclusion: string | null;
    startedAt: string | null;
    url: string | null;
    event: string | null;
  } | null;
}

interface CronsPayload {
  workflows: WorkflowRow[];
  githubError: string | null;
  vercelCrons: Array<{ path: string; schedule: string; description: string }>;
}

const CONCLUSION_STYLE: Record<string, string> = {
  success: 'bg-green-50 text-green-700',
  failure: 'bg-red-50 text-red-700',
  cancelled: 'bg-amber-50 text-amber-700',
};

/** Vertaal een cron-expressie naar leesbaar Nederlands (best effort). */
function humanizeCron(expr: string, utcShift = false): string {
  const DAGEN: Record<string, string> = {
    '0': 'zo', '1': 'ma', '2': 'di', '3': 'wo', '4': 'do', '5': 'vr', '6': 'za', '1-5': 'ma-vr',
  };
  const [min = '', hour = '', dom = '*', , dow = '*'] = expr.split(' ');
  if (min.startsWith('*/') && hour === '*') return `elke ${min.slice(2)} min`;
  if (min.startsWith('*/') && hour !== '*') return `elke ${min.slice(2)} min (${hour}u)`;
  if (/^\d+$/.test(min) && /^\d+$/.test(hour)) {
    const h = utcShift ? (parseInt(hour, 10) + 2) % 24 : parseInt(hour, 10);
    const tijd = `${String(h).padStart(2, '0')}:${min.padStart(2, '0')}`;
    const dagen = dow && dow !== '*' ? (DAGEN[dow] ?? dow.split(',').map((d) => DAGEN[d] ?? d).join('/')) : dom !== '*' ? `dag ${dom}` : 'dagelijks';
    return `${dagen} ${tijd}`;
  }
  return expr;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m geleden`;
  if (mins < 60 * 48) return `${Math.round(mins / 60)}u geleden`;
  return `${Math.round(mins / 1440)}d geleden`;
}

export function AutomatiseringPage() {
  const [data, setData] = useState<CronsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const fetchCrons = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Niet ingelogd');
      const res = await fetch('/api/beheer/crons', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || 'Kon automatisering niet laden');
      }
      setData((await res.json()) as CronsPayload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kon automatisering niet laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCrons();
  }, [fetchCrons]);

  const failures = data?.workflows.filter((w) => w.lastRun?.conclusion === 'failure') ?? [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-xl font-semibold text-gray-900">Automatisering</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Alles wat vanzelf draait: agents en pijplijnen (GitHub) en de office-app-crons (Vercel).
          Inhoudelijke content/SEO-runs: zie het <Link to="/marketing/seo-content" className="text-indigo-600 hover:underline">SEO &amp; Content-logboek</Link>.
        </p>
      </div>

      <div className="p-6 pt-2 space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
        {isLoading && !data && <p className="text-sm text-gray-400">Laden…</p>}

        {data && (
          <>
            {failures.length > 0 && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">
                {failures.length} workflow(s) faalden bij de laatste run: {failures.map((f) => f.name).join(', ')}
              </div>
            )}
            {data.githubError && (
              <div className="bg-amber-50 text-amber-700 text-sm rounded-lg px-4 py-3">
                GitHub-data niet beschikbaar: {data.githubError}
              </div>
            )}

            <section className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-1">Agents &amp; pijplijnen (GitHub Actions)</h2>
              <p className="text-xs text-gray-400 mb-4">Tijden in Nederlandse tijd (zomertijd). Klik op een run voor de volledige log.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-4 font-medium">Workflow</th>
                      <th className="py-2 pr-4 font-medium">Schema</th>
                      <th className="py-2 pr-4 font-medium">Laatste run</th>
                      <th className="py-2 font-medium">Uitkomst</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.workflows.map((wf) => (
                      <Fragment key={wf.path}>
                      <tr
                        className="border-b border-gray-50 cursor-pointer hover:bg-gray-50"
                        onClick={() => setOpenRow(openRow === wf.path ? null : wf.path)}
                      >
                        <td className="py-2.5 pr-4 text-gray-900">
                          <span className="text-gray-300 mr-1.5">{openRow === wf.path ? '▾' : '▸'}</span>
                          {wf.name}
                          {wf.state !== 'active' && <span className="ml-1.5 text-xs text-amber-600">({wf.state})</span>}
                        </td>
                        <td className="py-2.5 pr-4 text-gray-600 whitespace-nowrap">
                          {wf.schedules.length ? wf.schedules.map((s) => humanizeCron(s, true)).join(' + ') : 'handmatig'}
                        </td>
                        <td className="py-2.5 pr-4 whitespace-nowrap">
                          {wf.lastRun?.url ? (
                            <a href={wf.lastRun.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
                              {timeAgo(wf.lastRun.startedAt)}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2.5">
                          {wf.lastRun ? (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${CONCLUSION_STYLE[wf.lastRun.conclusion ?? ''] ?? 'bg-gray-100 text-gray-600'}`}>
                              {wf.lastRun.status === 'in_progress' ? 'draait nu' : wf.lastRun.conclusion ?? wf.lastRun.status}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">nog nooit gedraaid</span>
                          )}
                        </td>
                      </tr>
                      {openRow === wf.path && (
                        <tr className="border-b border-gray-50 bg-gray-50/60">
                          <td colSpan={4} className="py-3 px-4">
                            <p className="text-xs text-gray-400 mb-1.5 font-mono">{wf.path}</p>
                            <p className="text-sm text-gray-600 whitespace-pre-line max-w-3xl">
                              {wf.description || 'Geen beschrijving in de workflow-yml — voeg een #-commentblok toe onder de name-regel.'}
                            </p>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-1">Office-app-crons (Vercel) — {data.vercelCrons.length}</h2>
              <p className="text-xs text-gray-400 mb-4">
                Schema's in UTC. Uitval van kritieke crons verschijnt als alert in key-metrics; dit is de volledige inventaris.
              </p>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-1.5">
                {data.vercelCrons.map((c) => (
                  <div key={c.path} className={openRow === c.path ? 'md:col-span-2 xl:col-span-3' : ''}>
                    <div
                      className="flex items-baseline justify-between gap-3 text-sm border-b border-gray-50 py-1 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                      onClick={() => setOpenRow(openRow === c.path ? null : c.path)}
                    >
                      <span className="text-gray-700 truncate">
                        <span className="text-gray-300 mr-1">{openRow === c.path ? '▾' : '▸'}</span>
                        {c.path.replace('/api/cron/', '')}
                      </span>
                      <span className="text-gray-400 text-xs whitespace-nowrap">{humanizeCron(c.schedule)}</span>
                    </div>
                    {openRow === c.path && (
                      <div className="bg-gray-50/60 rounded-lg p-3 my-1.5">
                        <p className="text-xs text-gray-400 mb-1.5 font-mono">{c.path} · {c.schedule} (UTC)</p>
                        <p className="text-sm text-gray-600 whitespace-pre-line max-w-3xl">
                          {c.description || 'Geen beschrijving — voeg een docblock toe aan het cron-bestand en draai scripts/generate-cron-descriptions.mjs.'}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
